import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FileCode2, FileText, ImageIcon, ArrowLeft, UploadCloud } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function UploadExistingCard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [customer, setCustomer] = useState("");
  const [uploading, setUploading] = useState(false);

  const OPTIONS = [
    { key: "svg", label: "SVG", icon: FileCode2, note: t("upload.svgNote"), accept: ".svg" },
    { key: "pdf", label: "PDF", icon: FileText, note: t("upload.pdfNote"), accept: ".pdf" },
    { key: "image", label: "JPG / PNG", icon: ImageIcon, note: t("upload.imageNote"), accept: ".jpg,.jpeg,.png" },
  ];

  const handleUpload = async (e, key) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("customer", customer);
      const { data } = await api.post("/designs/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("upload.loadedAs", { n: data.designNumber }));
      navigate(`/designer/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("upload.uploadFailed"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-8 py-10" data-testid="upload-existing-page">
      <button onClick={() => navigate("/")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800" data-testid="back-to-home-button">
        <ArrowLeft className="h-4 w-4" /> {t("upload.backHome")}
      </button>
      <h1 className="mt-4 font-display text-2xl font-700 text-slate-900">{t("upload.title")}</h1>
      <p className="mt-1.5 text-sm text-slate-500">{t("upload.subtitle")}</p>

      <div className="mt-6 max-w-xs">
        <Label>{t("upload.customer")} ({t("common.optional")})</Label>
        <Input className="mt-1.5" value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder={t("upload.customerPlaceholder")} data-testid="upload-customer-input" />
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        {OPTIONS.map(({ key, label, icon: Icon, note, accept }) => (
          <label key={key} className="bhf-card flex cursor-pointer flex-col items-center gap-3 p-6 text-center transition-shadow hover:shadow-lg" data-testid={`upload-option-${key}`}>
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
              <Icon className="h-6 w-6" />
            </div>
            <div className="text-sm font-semibold text-slate-800">{label}</div>
            <div className="text-xs text-slate-500">{note}</div>
            <div className="mt-1 flex items-center gap-1 text-xs font-medium text-indigo-600"><UploadCloud className="h-3.5 w-3.5" /> {t("common.chooseFile")}</div>
            <input type="file" accept={accept} className="hidden" disabled={uploading} onChange={(e) => handleUpload(e, key)} />
          </label>
        ))}
      </div>

      <div className="mt-8 rounded-lg border border-dashed border-slate-200 p-4 text-xs leading-relaxed text-slate-500">
        <p>{t("upload.hint")}</p>
      </div>
      {uploading && <div className="mt-4 text-sm text-indigo-600" data-testid="upload-loading">{t("upload.processingUpload")}</div>}
    </div>
  );
}
